import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Deal, DealStage } from '../../types';
import { deleteDeal, fetchDeal, updateDeal } from '../../services/api';
import { decodeId } from '../../utils/hashids';
import NotesList from '../shared/NotesList';
import TasksList from '../shared/TasksList';
import ActivityFeed from '../shared/ActivityFeed';

type Tab = 'notes' | 'tasks' | 'timeline';

const DISPLAY_STAGES: { key: DealStage; label: string }[] = [
  { key: 'prospecting', label: 'OPEN' },
  { key: 'negotiation', label: 'NEGOTIATION' },
  { key: 'proposal',    label: 'PROPOSAL' },
  { key: 'closed_won',  label: 'WON' },
  { key: 'closed_lost', label: 'LOST' },
];

const STAGE_DISPLAY: Record<string, string> = {
  prospecting: 'OPEN', qualification: 'OPEN', negotiation: 'NEGOTIATION',
  proposal: 'PROPOSAL', closed_won: 'WON', closed_lost: 'LOST',
};

const initials = (name: string) =>
  name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

const fmtInr = (n?: number | null) =>
  n != null ? `₹${Number(n).toLocaleString('en-IN')}` : '—';

const DealDetail: React.FC = () => {
  const { id: idHash } = useParams<{ id: string }>();
  const numId          = decodeId(idHash) ?? 0;
  const history        = useHistory();
  const [deal, setDeal]     = useState<Deal | null>(null);
  const [tab, setTab]       = useState<Tab>('notes');
  const [error, setError]   = useState('');
  const [savingStage, setSavingStage] = useState(false);
  const [noteCount]  = useState(0);
  const [taskCount]  = useState(0);

  const reload = () =>
    fetchDeal(numId).then(setDeal).catch(() => setError('Deal not found'));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [idHash]);

  if (error) return <div className="ld-page"><p className="ld-empty">{error}</p></div>;
  if (!deal)  return <div className="ld-page"><div className="spinner">Loading…</div></div>;

  const displayStage  = STAGE_DISPLAY[deal.stage] ?? 'OPEN';
  const probability   = deal.probability ?? 0;
  const forecast      = Math.round(Number(deal.value) * probability / 100);

  const handleStage = async (stage: DealStage) => {
    setSavingStage(true);
    try { setDeal(await updateDeal(deal.id, { stage })); }
    finally { setSavingStage(false); }
  };

  const handleMarkWon  = () => handleStage('closed_won');
  const handleMarkLost = () => handleStage('closed_lost');

  const handleDelete = async () => {
    if (!window.confirm(`Delete deal "${deal.title}"?`)) return;
    await deleteDeal(deal.id);
    history.push('/deals');
  };

  return (
    <div className="ld-page">
      <button className="breadcrumb-back" onClick={() => history.push('/deals')}>
        ← Back to Deals
      </button>

      <div className="dd-layout">
        {/* ── MAIN ── */}
        <div className="dd-main">

          {/* Header card */}
          <div className="dd-header-card">
            <h1 className="dd-title">{deal.title}</h1>
            <div className="dd-badges">
              <span className="dd-stage-pill">{displayStage}</span>
              {probability > 0 && <span className="dd-prob-pill">{probability}%</span>}
              <span className="dd-value-pill">{fmtInr(deal.value)}/mo</span>
            </div>
            <div className="dd-meta-row">
              {deal.organization && (
                <span className="dd-org-link">{deal.organization.name} →</span>
              )}
              {deal.contact && (
                <span className="dd-contact-meta">Contact: {deal.contact.name}</span>
              )}
            </div>
            <div className="dd-actions">
              <button className="btn btn-secondary" onClick={() => history.push(`/deals`)}>Edit</button>
              <button className="dd-btn-won" onClick={handleMarkWon} disabled={savingStage}>
                Mark Won ✓
              </button>
              <button className="dd-btn-lost" onClick={handleMarkLost} disabled={savingStage}>
                Mark Lost ✗
              </button>
            </div>
          </div>

          {/* Stage selector */}
          <div className="dd-stage-bar">
            {DISPLAY_STAGES.map(s => {
              const active = displayStage === s.label;
              return (
                <button
                  key={s.key}
                  className={`dd-stage-tab${active ? ' dd-stage-tab-active' : ''}${s.label === 'WON' ? ' dd-stage-won' : s.label === 'LOST' ? ' dd-stage-lost' : ''}`}
                  onClick={() => !active && handleStage(s.key)}
                  disabled={savingStage}
                >
                  {s.label}
                </button>
              );
            })}
          </div>

          {/* Deal info */}
          <div className="dd-info-card">
            <div className="dd-info-grid">
              <div className="dd-info-col">
                <div className="dd-info-label">Deal Value</div>
                <div className="dd-info-big">{fmtInr(deal.value)}/mo</div>
              </div>
              <div className="dd-info-col">
                <div className="dd-info-label">Probability: {probability}%</div>
                <input
                  type="range" min={0} max={100} value={probability}
                  className="cvt-slider"
                  onChange={e => updateDeal(deal.id, { probability: parseInt(e.target.value) })
                    .then(setDeal)}
                />
              </div>
              <div className="dd-info-col">
                <div className="dd-info-label">Expected Close</div>
                <div className="dd-info-val">{deal.expected_close_date ?? '—'}</div>
              </div>
              <div className="dd-info-col">
                <div className="dd-info-label">Assigned To</div>
                <div className="dd-assignee">
                  {deal.assigned_user
                    ? <><span className="dd-assignee-avatar">{initials(deal.assigned_user.name)}</span>{deal.assigned_user.name}</>
                    : <em className="ld-empty">Unassigned</em>}
                </div>
              </div>
            </div>
          </div>

          {/* Notes / Tasks tabs */}
          <div className="dd-tab-card">
            <div className="dd-tabs">
              {([
                ['notes',    'Notes',    noteCount],
                ['tasks',    'Tasks',    taskCount],
                ['timeline', 'Timeline', 0],
              ] as [Tab, string, number][]).map(([t, label, cnt]) => (
                <button key={t} className={`dd-tab${tab === t ? ' dd-tab-active' : ''}`} onClick={() => setTab(t)}>
                  {label} {cnt > 0 ? cnt : 0}
                </button>
              ))}
            </div>

            <div className="dd-tab-body">
              {tab === 'notes'    && <NotesList    subjectType="deals" subjectId={deal.id} />}
              {tab === 'tasks'    && <TasksList    subjectType="deals" subjectId={deal.id} />}
              {tab === 'timeline' && <ActivityFeed subjectType="deals" subjectId={deal.id} />}
            </div>
          </div>
        </div>

        {/* ── SIDEBAR ── */}
        <div className="dd-sidebar">

          {/* Deal Summary */}
          <div className="ld-card">
            <div className="ld-card-title">Deal Summary</div>
            <div className="dd-summary-value">{fmtInr(deal.value)}<span className="dd-summary-mo">/mo</span></div>
            <div className="dd-summary-row">
              <span className="dd-summary-label">Probability:</span>
              <span>{probability}%</span>
            </div>
            <div className="dd-summary-row">
              <span className="dd-summary-label">Forecast Revenue:</span>
              <span style={{ color: 'var(--teal)', fontWeight: 600 }}>{fmtInr(forecast)}/mo</span>
            </div>
          </div>

          {/* Organization */}
          <div className="ld-card">
            <div className="ld-card-title">Organization</div>
            {deal.organization ? (
              <>
                <div className="dd-org-name">{deal.organization.name}</div>
                {(deal.organization.industry || deal.organization.territory) && (
                  <div className="dd-org-sub">
                    {[deal.organization.industry, deal.organization.territory].filter(Boolean).join(' · ')}
                  </div>
                )}
                {deal.organization.gst_number && (
                  <div className="dd-org-gst">GST: {deal.organization.gst_number}</div>
                )}
              </>
            ) : (
              <p className="detail-info-empty">No organisation linked. Convert a lead to link one.</p>
            )}
          </div>

          {/* Primary Contact */}
          <div className="ld-card">
            <div className="ld-card-title">Primary Contact</div>
            {deal.contact ? (
              <>
                <div className="dd-contact-name">{deal.contact.name}</div>
                {deal.contact.phone && (
                  <a className="dd-contact-phone" href={`tel:${deal.contact.phone}`}>
                    {deal.contact.phone}
                  </a>
                )}
                {deal.contact.email && (
                  <div className="dd-contact-email">{deal.contact.email}</div>
                )}
              </>
            ) : (
              <p className="detail-info-empty">No contact linked. Convert a lead to link one.</p>
            )}
          </div>

          {/* Recent Timeline */}
          <div className="ld-card">
            <div className="ld-card-title">Recent Timeline</div>
            <ActivityFeed subjectType="deals" subjectId={deal.id} compact />
          </div>

          {/* Danger zone */}
          <div className="ld-card">
            <button className="btn btn-danger" style={{ width: '100%' }} onClick={handleDelete}>
              Delete Deal
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealDetail;
